/**
 * useReveal - IntersectionObserver-based "in-view" hook.
 *
 * Returns a ref to attach to any element with the `.reveal`,
 * `.reveal-soft`, `.reveal-x-left`, or `.reveal-x-right` class.
 * The hook adds the `.in` class when the element enters the viewport.
 *
 * Set `--d: <ms>` as inline style to stagger the transition delay.
 *
 * Example:
 *   const ref = useReveal();
 *   return <div ref={ref} className="reveal" style={{"--d":"120ms"}}>…</div>;
 */
import { useEffect, useRef } from "react";

export interface UseRevealOptions {
  threshold?: number;
  rootMargin?: string;
}

export function useReveal<T extends HTMLElement = HTMLDivElement>(opts: UseRevealOptions = {}) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            el.classList.add("in");
            io.unobserve(el);
          }
        }
      },
      {
        threshold: opts.threshold ?? 0.15,
        rootMargin: opts.rootMargin ?? "0px 0px -80px 0px",
      },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [opts.threshold, opts.rootMargin]);
  return ref;
}
